import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { Button } from '../../components/form/Button';
import CrossIcon from '../../assets/icons/CrossIcon';
import SingleDocumentIcon from '../../assets/icons/SingleDocumentIcon';
import { useIgnoreDuplicateMutation } from '../../api/duplicateApiSlice';
import { useToast } from '../../components/toast/ToastContext';
import LoadingCircleIcon from '../../assets/icons/LoadingCircleIcon';
import { ActionType, Column, TableConfig } from '../../types/types';
import { DropResult } from '@hello-pangea/dnd';
import DataTable from '../../components/listing/DataTable';

type ContactDuplicate = {
  id: number;
  firstName: string;
  lastName: string;
  signedUrl?: string;
  email?: string;
};

type OrgDuplicate = {
  id: number;
  name: string;
};
interface IgnoreDuplicatePopup {
    close: () => void;
    // data: any[];
    data: ContactDuplicate[] | OrgDuplicate[];
    scope: string;
    refetch: () => void;
}

const IgnoreDuplicatePopup: React.FC<IgnoreDuplicatePopup> = ({ close, data, scope, refetch }) => {
    const {theme} = useTheme();
    const showToast = useToast();
    // const [saveLoad, setSaveLoad] = useState(false);
    const [loadingId, setLoadingId] = useState<number | null>(null);

    const sourceID = data[0].id ?? null;
    const [ignoreDuplicate, {isLoading, isSuccess, error}] = useIgnoreDuplicateMutation();

  const columns: Column[] =
  scope === "contact"
    ? [
        { id: "primaryUser", label: "Name", isSortable: true },
        { id: "email", label: "Email", isSortable: true },
      ]
    : [
        { id: "org_ID", label: "Organization ID", isSortable: true },
        { id: "name", label: "Organization Name", isSortable: true },
      ];

const tableData =
  scope === "contact"
    ? (data as ContactDuplicate[]).map((item) => ({
        contactId: item.id,
        contactFirstName: item.firstName,
        contactLastName: item.lastName,
        contactSignedUrl: item.signedUrl,
        email: item.email,
        sourceID: sourceID,
      }))
    : (data as OrgDuplicate[]).map((item) => ({
        org_ID: item.id,
        name: item.name,
        sourceID: sourceID,
      }));

 const STORAGE_KEY = `tableConfig_ignore`;
  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
        visibleColumns: columns.filter(col => col.id !== "id").map(col => col.id),
        page: 1,
        perPage: 10,
        sort: undefined,
        filters: {},
        query: "",
        columnOrder: columns.map(col => col.id),
      };
  });

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const newColumnOrder = Array.from(tableConfig.columnOrder);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);
      setTableConfig(prev => ({ ...prev, columnOrder: newColumnOrder }));
    },
    [tableConfig.columnOrder]
  );

  const handelSubmit = async (id: number) => {
      setLoadingId(id);
      const payLoad = {
        moduleCriteria: scope,
        sourceId: sourceID,
        duplicateId: id
      }

      try {
          const result = await ignoreDuplicate(payLoad).unwrap(); 
          showToast('Success', 'Item ignored successfully', 'success');
          close();
          refetch();
        } catch (err) {
          console.error('Failed to ignore:', err);
          showToast('Error', 'Failed to ignore item', 'error');
          setLoadingId(null);
        }
    }

  const handleAction = useCallback(async (action: ActionType, id: string) => {
      const numericId = Number(id);
  
      if (action === 'ignore') {
        setLoadingId(numericId);
        handelSubmit(numericId);
        return;
      }

    }, [handelSubmit]);

  return (
    <Overlay>
      <Container theme={theme}>
        <CloseIcon onClick={close}>
            <CrossIcon />
        </CloseIcon>
        <Body>
          <Title theme={theme} className='text-center'>
            Merge Duplicates
          </Title>
          <Subtitle theme={theme} className='text-center'>
            Identify, review, and merge duplicate records to maintain accuracy, avoid <br />confusion, and keep your database organized.
          </Subtitle>

          <DataTable
              routeKey='contacts'
              title='Ignore'
              // profiledName
              hideActionColumn={false}
              columns={columns}
              data={tableData}
              columnOrder={tableConfig.columnOrder}
              visibleColumns={tableConfig.visibleColumns}
              sort={tableConfig.sort}
              noDataTitle="No Contact Found"
              noDataSubtitle="Your Duplicate list is currently empty."
              onDragEnd={handleDragEnd}
              loading={false}
              perPage={tableConfig.perPage}
              onAction={handleAction}
              minWidth="1000px"
              loadingID={loadingId}
            />
        </Body>
      </Container>
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '1098px',
  background: theme.CorporateTheme.colors.background,
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',
  fontFamily: '"Segoe UI", sans-serif',
  padding: '40px',
  position: 'relative',
}));

const CloseIcon = styled.span`
    position: absolute;
    right: 10px;
    top: 10px;
    cursor: pointer;

`;

const Body = styled.div`
  padding-bottom: 24px;
  // padding-top: 0px;
`;

const Title = styled('h2')(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: '"Raleway", sans-serif',
  color: theme.CreateFormTheme.colors.primary,
  marginBottom: '12px',
}));

const Subtitle = styled('p')(({ theme }) => ({
  fontSize: '14px',
  color: theme.CorporateTheme.colors.textSecondary,
  fontWeight: 500,
  lineHeight: 1.5,
  fontFamily: '"Raleway", sans-serif',
}));

const Table = styled.table<{ width?: string }>`
  // min-width: ${({ width }) => (width ? `${width}px` : "1200px")};
  width: 100%;
  border-collapse: collapse;
  thead {
    background: ${({theme}) => theme.CorporateTheme.colors.headerBG};
    height: 45px;
    position: sticky;
    top: 0;
    z-index: 2;
    th {
      font-size: 13px;
      font-weight: 600;
      color: ${({theme}) => theme.colors.contentText}
    }
  }
  tbody {
    tr {
      border-bottom: 1px solid ${({theme}) => theme.CorporateTheme.colors.border};
      height: 46px;
      td {
        font-size: 13px;
        font-weight: 400;
        color: ${({theme}) => theme.CorporateTheme.colors.textSecondary};
      }
    }
  }
`;

export default IgnoreDuplicatePopup